import TalaeraRoutes from "components/routes/TalaeraRoutes";
import TalaeraSpinner from "components/TalaeraSpinner/TalaeraSpinner";
import { isServerError, isServerErrorResponse } from "model/general";
import { OAuthProvider, OAuthStep } from "model/oauth";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { populateUserSession } from "services/authUtil";
import { logError } from "services/logging";
import { confirmOAuth, OAuthUserInfo, startOAuth, verifyEmail } from "services/oauth";
import { isEnumMember } from "utils/enums";

/**
 * Custom hook that handles user authentication and navigation after sign-in.
 */
function useSignin(): () => Promise<void> {
  const location = useLocation();
  const navigate = useNavigate();

  const action = useCallback(async () => {
    await new Promise((resolve, reject) => populateUserSession(resolve, reject));
    navigate(TalaeraRoutes.ROOT.path + (location.hash || ""));
  }, [location.hash, navigate]);

  return action;
}

interface Props {
  provider: OAuthProvider;
  step: OAuthStep;
}

function ValidatedOAuthContent({ provider, step }: Props) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const signIn = useSignin();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const isPro = searchParams.get("is_pro") === "true";
  const pricingModelCode = searchParams.get("code");

  useEffect(() => {
    const action = async () => {
      let userInfo: OAuthUserInfo | undefined = undefined;
      try {
        const oauthParams = Object.fromEntries(searchParams.entries());
        switch (step) {
          case OAuthStep.Start: {
            const { url } = await startOAuth(provider, isPro, pricingModelCode);
            window.location.href = url; // Redirect to the OAuth provider
            return;
          }
          case OAuthStep.Confirm: {
            const confirmationResults = await confirmOAuth(provider, oauthParams);
            if (confirmationResults.verify_email) {
              setError("SocialAuth.clickVerificationLink");
              return;
            }
            userInfo = confirmationResults.user;
            break;
          }
          case OAuthStep.VerifyEmail: {
            if (!oauthParams?.token) {
              setError("SocialAuth.invalidToken");
              return;
            }
            userInfo = await verifyEmail(oauthParams.token);
            break;
          }
        }
        if (!userInfo) {
          throw new Error("No user info received from OAuth provider"); // Should never happen
        }

        if (userInfo.mfa) {
          navigate(TalaeraRoutes.SIGN_IN.path, {
            state: { mfaToken: userInfo.token, email: userInfo.email },
          });
          return;
        } else {
          await signIn();
        }
      } catch (e) {
        logError("An error during OAuth", e);
        let message = "SocialAuth.genericOAuthError";
        let serverError = e;
        if (isServerErrorResponse(serverError)) {
          serverError = serverError.errors[0];
        }
        if (isServerError(serverError)) {
          message = `SocialAuth.${serverError.error_code}`;
        }
        setError(message);
      }
    };
    action();
  }, [navigate, provider, signIn, step, searchParams, isPro, pricingModelCode]);

  if (error) {
    return (
      <Navigate to={TalaeraRoutes.SIGN_IN.path} replace state={{ error: t(error) }} />
    );
  }

  return <TalaeraSpinner />;
}

export default function OAuthLoadingPage() {
  const { provider, step } = useParams();
  // FIXME: Rework this when (if) [zod] is added to the project.
  if (
    !provider ||
    !isEnumMember(OAuthProvider, provider) ||
    !step ||
    !isEnumMember(OAuthStep, step)
  ) {
    return <Navigate to={TalaeraRoutes.NOT_FOUND.path} replace />;
  }

  return (
    <ValidatedOAuthContent
      provider={provider as OAuthProvider}
      step={step as OAuthStep}
    />
  );
}
